// src/pages/SakshamBharatHome.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

// Inline SVG for Indian Flag
const IndianFlagSVG = ({ className = "inline h-4 w-6" }) => (
  <svg
    className={className}
    viewBox="0 0 16 12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="0" width="16" height="4" fill="#FF9933" />
    <rect y="4" width="16" height="4" fill="white" />
    <circle cx="8" cy="6" r="1.5" fill="#006BA9" stroke="white" strokeWidth="0.1" />
    <rect y="8" width="16" height="4" fill="#138808" />
  </svg>
);

const SakshamBharatHome = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLogged = authState?.isLogged || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/sakshambharat");
  };

  const groupNames = [
    { label: "SAKSHAM MSME", emoji: "🏢", link: "/msme" },
    { label: "SAKSHAM Krishi & Gaon", emoji: "🌾🏘️", link: "/agrilink" },
    { label: "SAKSHAM Manufacturing", emoji: "🏭", link: "/syntramfg" },
    { label: "SAKSHAM Startup", emoji: "🚀", link: "/startup" },
    { label: "SAKSHAM Women", emoji: "👩‍💼", link: "/sheearns" },
    { label: "SAKSHAM Yuva", emoji: "🔥", link: "/Yuvasolver" },
    { label: "SAKSHAM Teenagers", emoji: "📱", link: "/Golden7" },
    { label: "SAKSHAM Shramik", emoji: "💪", link: "/sakshamshram" },
    { label: "SAKSHAM Teacher", emoji: "👩‍🏫", link: "/teachable" },
    { label: "SAKSHAM Government", emoji: "🏛️", link: "/sheetal" },
    { label: "Responsible Citizen", emoji: "👥", link: "/rim" },
    { label: "SMILE", emoji: "📖🌈", link: "/smile" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-green-950 text-emerald-100 font-sans">
      {/* Tricolor Stripe */}
      <div className="h-1 bg-gradient-to-r from-orange-500 via-white to-emerald-500 shadow-lg"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-orange-900 border-b border-orange-800 px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex flex-col">
          <div className="text-2xl font-bold text-white flex items-center gap-1">
            <IndianFlagSVG /> SAKSHAM Bharat
          </div>
          <div className="text-[11px] text-orange-200/70">
            Skilled And Keen, Systematic Hands Achieves More!
          </div>
          <div className="text-[11px] text-white/90">
            Extending Digital Empowerment of Indians for Continuous Digital Income, Learning & Productivity!
          </div>
        </div>

        {/* Desktop user controls */}
        <div className="hidden md:flex items-center gap-3">
          {isLogged ? (
            <>
              <div className="text-sm text-white">👋 {userName}</div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-md bg-red-600 hover:bg-red-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-3 py-1 rounded-full border border-white text-sm hover:bg-white/10"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-3 py-1 rounded-full bg-emerald-500 text-white text-sm hover:bg-emerald-400"
              >
                Register
              </button>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-2xl text-white"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-orange-900 border-b border-orange-800 px-4 py-3 animate-[fadeIn_0.3s_ease-in-out]">
          {isLogged ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-white font-medium flex items-center gap-1">
                Welcome, {userName} <IndianFlagSVG className="h-3 w-4" />
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-md bg-red-600 hover:bg-red-700"
              >
                ⏻
              </button>
            </div>
          ) : (
            <div className="flex justify-center gap-6">
              <Link to="/login" className="text-white">Login</Link>
              <Link to="/signup" className="text-white">Register</Link>
            </div>
          )}
        </div>
      )}

      {/* KPI Section */}
      <div className="bg-white/5 text-[11px] sm:text-sm py-2 px-2 text-center text-orange-300 leading-relaxed">
        <span className="text-amber-400 font-semibold">🌏 Transformative Framework:</span> Empowering <span className="text-emerald-300 font-semibold">10M+ Youth & Women Jobs</span> • 
        <span className="text-amber-300"> 💰 $250B GDP Boost</span> • 
        <span className="text-emerald-300"> 📈 ₹18K Per Capita Rise</span> • 
        <span className="text-orange-400">🏢 4Cr MSMEs</span> • 
        <span className="text-green-300">🌾 15Cr Farmers</span> • 
        <span className="text-blue-300">⚙️ 30Cr+ Workers</span> • 
        <span className="text-pink-300">👩‍💼 35Cr Women</span> • 
        <span className="text-yellow-300">👩‍🏫 1.5Cr Teachers</span> • 
        <span className="text-teal-300">🎓 40Cr Students</span> • 
        <span className="text-orange-300">🏛️ Govt, PSUs & GLBs United</span>. 
        <span className="text-emerald-400 font-semibold">⚡ Mission-Mode Implementation Ahead!</span>
        <span className="text-sky-400 font-semibold">🌍 Empowering the Global South with support from nations like Japan</span>
      </div>

      {/* Tabs */}
      <div className="mt-1 mb-1 px-3 py-2 border-b border-emerald-700 bg-white/5">
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-5 gap-1 place-items-center">
          {groupNames.map((group) => (
            <button
              key={group.label}
              onClick={() => navigate(group.link)}
              className="flex flex-col items-center justify-center w-full px-2 py-2 rounded-md text-xs sm:text-sm font-medium transition bg-emerald-800/50 border border-white/20 hover:bg-emerald-700/70"
            >
              <span className="text-lg">{group.emoji}</span>
              <span className="mt-1 text-center">{group.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Vision Section */}
      <section className="relative bg-gradient-to-br from-orange-950 via-gray-950 to-emerald-950 py-6 px-3 sm:py-8 sm:px-6 flex flex-col items-center justify-center text-center min-h-[350px] overflow-hidden">

        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;utf8,<svg width=\\'60\\' height=\\'60\\' viewBox=\\'0 0 60 60\\' xmlns=\\'http://www.w3.org/2000/svg\\'><g fill=%27%23FF9933%27 fill-opacity=%270.1%27><path d=\\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM36 4V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\\'/></g></svg>')]"></div>
        <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-orange-500 to-emerald-500"></div>
        <div className="absolute right-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-orange-500 to-emerald-500"></div>
        <div className="relative z-10 max-w-3xl w-full animate-[fadeInUp_0.8s_ease-out] px-2">
          {/* <div className="flex justify-center items-center mb-3">
            <IndianFlagSVG className="h-5 w-7 mr-2" />
            <h2 className="text-m sm:text-lg md:text-xl font-bold bg-gradient-to-r from-orange-400 via-amber-300 to-emerald-400 bg-clip-text text-transparent tracking-wide">
              SAKSHAM Bharat Vision
            </h2>
            <IndianFlagSVG className="h-5 w-7 ml-2" />
          </div> */}
          <div className="text-center px-3">
            <p className="text-xs sm:text-sm md:text-base font-serif italic text-emerald-200/90 mb-5 leading-relaxed tracking-wide flex flex-col items-center justify-center">
              {/* <span className="flex items-center justify-center gap-2 mb-2">
                <IndianFlagSVG className="h-5 w-7" />
                <span className="font-semibold text-amber-300">Vision:</span>
              </span> */}
              <span className="max-w-3xl text-center">
                Imagine a radiant dawn over Bharat, where <span className="text-emerald-300 font-medium">SAKSHAM Bharat</span> ignites every soul — from MSME visionaries and Krishi stewards to innovators, women trailblazers, Yuva dreamers, shramiks, teachers, and citizens.  
                United in purpose, we rise on digital wings of <span className="text-amber-300">skill</span>, <span className="text-emerald-300">innovation</span>, and <span className="text-sky-300">determination</span> — forging the path toward  
                <span className="text-orange-300 font-semibold"> Viksit Bharat 2047</span>, <span className="text-emerald-400 font-semibold"> Atmanirbhar Bharat</span>, <span className="text-amber-400 font-semibold"> Make In India</span>, and <span className="text-sky-400 font-semibold"> Digital India</span>.
              </span>
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            {[
              { icon: "🚀", label: "Viksit Bharat 2047" },
              { icon: <IndianFlagSVG className="h-4 w-6" />, label: "Atmanirbhar Bharat" },
              { icon: "🏭", label: "Make In India" },
              { icon: "💻", label: "Digital India" },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-emerald-900/30 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-orange-500/30 flex flex-col items-center justify-center text-center hover:scale-105 transition-all"
              >
                <span className="text-lg sm:text-xl mb-1">{item.icon}</span>
                <span className="text-[10px] sm:text-xs md:text-sm font-semibold text-emerald-100">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
          <button className="mt-6 sm:mt-8 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-orange-500 to-emerald-500 text-white font-semibold rounded-full shadow-lg hover:scale-105 transition">
            Join the SAKSHAM Revolution <IndianFlagSVG className="h-4 w-5 inline ml-1" />
          </button>
        </div>
      </section>

      {/* Trusted Section */}
      {/* <section> */}
      <section className="sticky top-0 z-50 bg-orange-900 border-b border-orange-800 px-4 py-1 flex items-center justify-between shadow-lg">
        <div className="bg-white/5 text-xs sm:text-sm py-2 text-center text-orange-300">
          🔒 Trusted & Secure Platform – ISO 9001, ISO 27001, SOC-2 Certified
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-green-900 border-t border-emerald-800 text-emerald-300 py-4 text-center text-sm">
        © 2025 Think5Whys Global DeepTech Solutions. All rights reserved. <IndianFlagSVG />
      </footer>
    </div>
  );
};

export default SakshamBharatHome;
